<template>
  <div class="p-tab">
    <Tabs :model-value="activeId" :size="resolvedSize" @update:model-value="onTabChange">
      <Tab
        v-for="t in tabMeta"
        :key="t.id"
        :value="t.id"
        :badge="t.notification && t.notification > 0 ? t.notification : undefined"
        @click="$emit('tab-selected', t.id)"
      >
        {{ t.title }}
      </Tab>
    </Tabs>
    <div class="p-tab__content">
      <keep-alive>
        <slot />
      </keep-alive>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, ref, useSlots, watch, type ComputedRef } from 'vue'
import { Tabs, Tab } from '@p-utilities/vue'

interface TabMeta {
  id: string
  title?: string
  notification?: number
}

const props = defineProps<{
  modelValue?: string | number
  selected?: string | number
  size?: 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large'
  icon?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'tab-selected', value: string): void
}>()

const slots = useSlots()

// Read tab metadata from slot children (each child is a PTabPanel with `id` and `title` props).
const tabMeta = computed<TabMeta[]>(() => {
  const items = slots.default?.() ?? []
  return items.map((vnode) => (vnode.props ?? {}) as TabMeta).filter((p) => !!p.id)
})

const activeId = ref<string>(
  String(props.modelValue ?? props.selected ?? tabMeta.value[0]?.id ?? ''),
)

watch(
  () => props.modelValue,
  (v) => {
    if (v !== undefined && String(v) !== activeId.value) {
      activeId.value = String(v)
    }
  },
)

const onTabChange = (v: string) => {
  activeId.value = v
  emit('update:modelValue', v)
}

provide<ComputedRef<string>>(
  'pTabActiveId',
  computed(() => activeId.value),
)

const SIZE_MAP: Record<string, 'small' | 'medium' | 'large'> = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
}
const resolvedSize = computed<'small' | 'medium' | 'large'>(() => {
  const s = props.size
  if (!s) return 'small'
  if (s === 'small' || s === 'medium' || s === 'large') return s
  return SIZE_MAP[s] ?? 'small'
})
</script>
